import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { FormFieldErrorsState } from '../../shared/design-system/form-field-errors';
import { PAYMENTS_LIST_PAGE_STYLES } from './payments-list-page.utils';
import { PaymentProviderRow, PaymentsAdminService } from './payments-admin.service';
import { getPaymentProviderUiProfile } from './payment-provider-ui-profiles';
import { isParsianProviderSettingsValid, parseParsianProviderSettings } from './payment-provider-settings.models';
import { PaymentProviderSettingsPanelComponent } from './payment-provider-settings-panel.component';

@Component({
  standalone: true,
  selector: 'ain-payments-providers',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    SelectModule,
    TagModule,
    AinCanAccessDirective,
    PaymentProviderSettingsPanelComponent
  ],
  template: `
    <ain-page [eyebrow]="t('payments.eyebrow')" [title]="t('payments.providers.title')" [description]="t('payments.providers.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'payments.providers.help.page.title'"
        [descriptionKey]="'payments.providers.help.page.description'"
        [bulletKeys]="[
          'payments.providers.help.page.bullets.scope',
          'payments.providers.help.page.bullets.actions',
          'payments.providers.help.page.bullets.filters'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('payments.providers.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('payments.providers.unavailable')" [message]="error() ?? ''" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="reload()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('payments.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'payments.providers.help.filters.title'"
                [descriptionKey]="'payments.providers.help.filters.description'" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('payments.providers.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('payments.providers.filters.status') }}</span>
                <p-select
                  [options]="statusFilterOptions"
                  optionValue="value"
                  [(ngModel)]="statusFilter"
                  name="statusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          <div class="section-title">
            <span>{{ t('payments.providers.help.table.title') }}</span>
            <ain-info-hint
              [titleKey]="'payments.providers.help.table.title'"
              [descriptionKey]="'payments.providers.help.table.description'" />
          </div>
          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('payments.providers.states.empty')" [message]="t('payments.providers.states.emptyMessage')" />
          } @else {
            <p-table [value]="filteredRows()" dataKey="providerKey" [paginator]="true" [rows]="10">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('payments.providers.providerKey') }}</th>
                  <th>{{ t('payments.providers.displayName') }}</th>
                  <th>{{ t('columns.status') }}</th>
                  <th>{{ t('payments.providers.capabilities') }}</th>
                  <th ainCanAccess [ainCanAccessPermission]="'payments.providers'">{{ t('payments.portfolio.actions') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.providerKey }}</td>
                  <td>{{ row.displayName }}</td>
                  <td>
                    <p-tag
                      [value]="row.isEnabled ? t('payments.common.enabled') : t('payments.common.disabled')"
                      [severity]="row.isEnabled ? 'success' : 'warn'">
                    </p-tag>
                  </td>
                  <td>{{ row.capabilities.join(', ') || '-' }}</td>
                  <td class="actions-cell" ainCanAccess [ainCanAccessPermission]="'payments.providers'">
                    @if (!row.isEnabled) {
                      <button
                        pButton
                        type="button"
                        size="small"
                        [label]="t('payments.providers.enable')"
                        [disabled]="savingProviderKey() === row.providerKey"
                        (click)="openConfigureDialog(row, true)">
                      </button>
                    }
                    @if (row.isEnabled) {
                      <button
                        pButton
                        type="button"
                        size="small"
                        severity="secondary"
                        [label]="t('common.edit')"
                        [disabled]="savingProviderKey() === row.providerKey"
                        (click)="openConfigureDialog(row, null)">
                      </button>
                      <button
                        pButton
                        type="button"
                        size="small"
                        severity="warn"
                        [label]="t('payments.providers.disable')"
                        [disabled]="savingProviderKey() === row.providerKey"
                        (click)="disableProvider(row)">
                      </button>
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (actionError()) { <p class="error">{{ actionError() }}</p> }
        </section>
      }

      <p-dialog
        [header]="configureDialogTitle()"
        [modal]="true"
        [(visible)]="configureDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label>
            <span class="label-row">
              {{ t('payments.providers.providerKey') }}
              <ain-info-hint
                [titleKey]="'payments.providers.help.configure.title'"
                [descriptionKey]="'payments.providers.help.configure.description'" />
            </span>
            <input pInputText [ngModel]="configureModel.providerKey" name="configureProviderKey" [disabled]="true" />
          </label>
          <label>
            <span>{{ t('payments.providers.displayName') }}</span>
            <input pInputText [(ngModel)]="configureModel.displayName" name="configureDisplayName" />
          </label>

          <section class="gateway-section wide">
            <div class="section-heading">
              <span>{{ t('payments.providers.gateway.sectionTitle') }}</span>
              <ain-info-hint
                [titleKey]="'payments.providers.gateway.help.title'"
                [descriptionKey]="'payments.providers.gateway.help.description'" />
            </div>
            <ain-payment-provider-settings-panel
              [providerKey]="configureModel.providerKey"
              [settingsJson]="configureModel.settingsJson"
              [fieldErrors]="gatewayFieldErrors"
              (settingsJsonChange)="onGatewaySettingsJsonChange($event)" />
          </section>

          @if (showCallbackSecretField()) {
            <label class="wide">
              <span class="label-row">
                {{ t('payments.providers.callbackSecret') }}
                <ain-info-hint
                  [titleKey]="'payments.providers.help.callbackSecret.title'"
                  [descriptionKey]="'payments.providers.help.callbackSecret.description'" />
              </span>
              <input
                pInputText
                type="password"
                [(ngModel)]="configureModel.callbackSecret"
                name="configureCallbackSecret"
                [placeholder]="configureSecretRequired() ? '' : t('payments.providers.callbackSecretOptional')" />
            </label>
          }
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="configureDialogVisible = false"></button>
          <button
            pButton
            type="button"
            [label]="t('common.save')"
            [disabled]="savingProviderKey() !== null || !canSaveConfigure()"
            (click)="saveConfigure()">
          </button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .label-row { display: inline-flex; align-items: center; gap: 6px; }
    .gateway-section { margin-top: 4px; padding-top: 12px; border-top: 1px solid #e2e8f0; }
    .section-heading { display: flex; align-items: center; gap: 6px; font-weight: 600; margin-bottom: 10px; color: #334155; }
    ${PAYMENTS_LIST_PAGE_STYLES}
  `]
})
export class PaymentsProvidersComponent implements OnInit {
  private readonly api = inject(PaymentsAdminService);
  private readonly localizationService = inject(LocalizationService);

  @ViewChild(PaymentProviderSettingsPanelComponent) gatewaySettingsPanel?: PaymentProviderSettingsPanelComponent;

  readonly rows = signal<PaymentProviderRow[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly search = signal('');
  readonly savingProviderKey = signal<string | null>(null);
  readonly configureDialogTitle = signal('');
  readonly configureSecretRequired = signal(true);

  statusFilter: boolean | null = null;
  configureDialogVisible = false;
  configureTargetEnabled: boolean | null = null;
  configureModel = { providerKey: '', displayName: '', callbackSecret: '', settingsJson: null as string | null };
  readonly gatewayFieldErrors = new FormFieldErrorsState();

  readonly statusFilterOptions = [
    { value: true, labelKey: 'payments.common.enabled' },
    { value: false, labelKey: 'payments.common.disabled' }
  ];

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.actionError.set(null);
    this.api.listProviders().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('payments.providers.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): PaymentProviderRow[] {
    const value = this.search().trim().toLowerCase();
    return this.rows()
      .filter(row => this.statusFilter === null || row.isEnabled === this.statusFilter)
      .filter(row =>
        !value ||
        row.providerKey.toLowerCase().includes(value) ||
        row.displayName.toLowerCase().includes(value) ||
        row.capabilities.some(capability => capability.toLowerCase().includes(value)));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
  }

  openConfigureDialog(row: PaymentProviderRow, enable: boolean | null): void {
    this.configureTargetEnabled = enable;
    this.gatewayFieldErrors.clearAll();
    const profile = getPaymentProviderUiProfile(row.providerKey);
    this.configureModel = {
      providerKey: row.providerKey,
      displayName: row.displayName,
      callbackSecret: '',
      settingsJson: row.settingsJson ?? null
    };
    this.configureSecretRequired.set(enable === true && profile.requiresCallbackSecretOnEnable);
    this.configureDialogTitle.set(
      enable === true
        ? this.t('payments.providers.configureEnableTitle')
        : this.t('payments.providers.configureTitle'));
    this.actionError.set(null);
    this.configureDialogVisible = true;
  }

  showCallbackSecretField(): boolean {
    const profile = getPaymentProviderUiProfile(this.configureModel.providerKey);
    return profile.requiresCallbackSecretOnEnable || !!this.configureModel.callbackSecret.trim();
  }

  onGatewaySettingsJsonChange(settingsJson: string | null): void {
    this.configureModel.settingsJson = settingsJson;
  }

  canSaveConfigure(): boolean {
    if (!this.configureModel.providerKey.trim()) {
      return false;
    }

    if (this.configureSecretRequired() && !this.configureModel.callbackSecret.trim()) {
      return false;
    }

    const willEnable = this.configureTargetEnabled ?? true;
    return this.isGatewaySettingsSatisfiedForSave(willEnable);
  }

  /** Pure check for template bindings — must not write to signals. */
  private isGatewaySettingsSatisfiedForSave(willEnable: boolean): boolean {
    if (!willEnable) {
      return true;
    }

    if (this.gatewaySettingsPanel) {
      return this.gatewaySettingsPanel.isGatewaySettingsSatisfiedForSave(willEnable);
    }

    const profile = getPaymentProviderUiProfile(this.configureModel.providerKey);
    if (profile.gatewayFamily === 'parsian-pec') {
      return isParsianProviderSettingsValid(parseParsianProviderSettings(this.configureModel.settingsJson));
    }

    return true;
  }

  saveConfigure(): void {
    const isEnabled = this.configureTargetEnabled ?? true;
    if (!this.gatewaySettingsPanel?.validateGatewaySettings(isEnabled)) {
      return;
    }

    if (!this.canSaveConfigure()) {
      return;
    }

    this.upsertProvider(
      this.configureModel.providerKey,
      this.configureModel.displayName.trim() || this.configureModel.providerKey,
      isEnabled,
      this.configureModel.callbackSecret.trim(),
      this.configureModel.settingsJson,
      () => {
        this.configureDialogVisible = false;
      });
  }

  disableProvider(row: PaymentProviderRow): void {
    this.upsertProvider(row.providerKey, row.displayName, false, '', row.settingsJson ?? null, () => undefined);
  }

  private upsertProvider(
    providerKey: string,
    displayName: string,
    isEnabled: boolean,
    callbackSecret: string,
    settingsJson: string | null,
    onSuccess: () => void): void {
    this.savingProviderKey.set(providerKey);
    this.actionError.set(null);
    this.api.upsertProvider({ providerKey, displayName, isEnabled, callbackSecret, settingsJson }).subscribe({
      next: updated => {
        this.rows.update(items => items.map(item =>
          item.providerKey === updated.providerKey ? updated : item));
        this.savingProviderKey.set(null);
        onSuccess();
      },
      error: () => {
        this.savingProviderKey.set(null);
        this.actionError.set(this.t('payments.providers.errorSave'));
      }
    });
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
